// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Types and schemas for inventory-related data in the Hardfin application.
 * This module uses Zod for runtime type validation and TypeScript type inference.
 * Based on backend inventory response structures from Go.
 */
import { responseMetadataSchema } from '@/api/types';
import {
  sparseBatchSchema,
  sparseItemSchema,
  sparseLocationWithParentSchema,
} from '@/sparse/api/types';
import { z } from 'zod';

/**
 * Schema for the units of an inventory row at one exact location
 * Maps to Go InventoryEntry
 */
const inventoryEntrySchema = z.object({
  location: sparseLocationWithParentSchema,
  count: z.number(),
});

export type InventoryEntry = z.infer<typeof inventoryEntrySchema>;

/**
 * Schema for the assets of a group at one exact location
 * Maps to Go AssetInventoryEntry
 */
const assetInventoryEntrySchema = inventoryEntrySchema.extend({
  serials: z.array(z.string()),
});

/**
 * Schema for grouped asset inventory item
 * Maps to Go GroupedAssetInventoryItem - assets of one item and functional status at a site
 */
export const groupedAssetInventoryItemSchema = z.object({
  item: sparseItemSchema,
  functionalStatus: z.string(),
  location: sparseLocationWithParentSchema,
  inventory: z.array(assetInventoryEntrySchema),
});

export type GroupedAssetInventoryItem = z.infer<
  typeof groupedAssetInventoryItemSchema
>;

/**
 * Schema for bulk item inventory item, the stock of one bulk item at a site
 * Maps to Go BulkItemInventoryItem
 */
export const bulkItemInventoryItemSchema = z.object({
  item: sparseItemSchema,
  location: sparseLocationWithParentSchema,
  inventory: z.array(inventoryEntrySchema),
});

export type BulkItemInventoryItem = z.infer<typeof bulkItemInventoryItemSchema>;

/**
 * Returns the total count across every location of an inventory row
 */
export function getTotalCount(inventory: { count: number }[]): number {
  return inventory.reduce((sum, entry) => sum + entry.count, 0);
}

/**
 * Returns the count an inventory row holds at one exact site or zone
 */
export function getCountAtLocation(
  inventory: InventoryEntry[],
  locationId: string
): number {
  return (
    inventory.find((entry) => entry.location.id === locationId)?.count ?? 0
  );
}

/**
 * Schema for assembly inventory item, the unbound assemblies of one item at a site
 * Maps to Go AssemblyInventoryItem
 */
export const assemblyInventoryItemSchema = z.object({
  item: sparseItemSchema,
  location: sparseLocationWithParentSchema,
  inventory: z.array(inventoryEntrySchema),
});

/**
 * Schema for unified inventory response, one list of rows tagged with their kind
 * Maps to Go AllInventory
 */
const allInventorySchema = z.object({
  results: z.array(
    z.discriminatedUnion('kind', [
      bulkItemInventoryItemSchema.extend({ kind: z.literal('BULK_ITEM') }),
      groupedAssetInventoryItemSchema.extend({ kind: z.literal('ASSET') }),
      assemblyInventoryItemSchema.extend({ kind: z.literal('ASSEMBLY') }),
    ])
  ),
});

/**
 * Schema for bulk items-only inventory response
 * Maps to Go BulkItemsInventoryResponse
 */
const bulkItemsInventorySchema = z.object({
  results: z.array(bulkItemInventoryItemSchema),
});

/**
 * Schema for assets-only inventory response
 * Maps to Go AssetsInventoryResponse
 */
const assetsInventorySchema = z.object({
  results: z.array(groupedAssetInventoryItemSchema),
});

export type AssetsInventory = z.infer<typeof assetsInventorySchema>;

/**
 * Schema for assemblies-only inventory response
 * Maps to Go AssemblyInventoryResponse
 */
const assembliesInventorySchema = z.object({
  results: z.array(assemblyInventoryItemSchema),
});

/**
 * Complete API response schemas with standard envelope
 */

/**
 * Response schema for GET /v1/inventory/all
 */
export const allInventoryResponseSchema = z.object({
  data: allInventorySchema,
  metadata: responseMetadataSchema,
});

export type AllInventoryResponse = z.infer<typeof allInventoryResponseSchema>;

/**
 * Response schema for GET /v1/inventory/bulk-items
 */
export const bulkItemsInventoryResponseSchema = z.object({
  data: bulkItemsInventorySchema,
  metadata: responseMetadataSchema,
});

export type BulkItemsInventoryResponse = z.infer<
  typeof bulkItemsInventoryResponseSchema
>;

/**
 * Response schema for GET /v1/inventory/assets
 */
export const assetsInventoryResponseSchema = z.object({
  data: assetsInventorySchema,
  metadata: responseMetadataSchema,
});

export type AssetsInventoryResponse = z.infer<
  typeof assetsInventoryResponseSchema
>;

/**
 * Response schema for GET /v1/inventory/products (assemblies)
 */
export const assembliesInventoryResponseSchema = z.object({
  data: assembliesInventorySchema,
  metadata: responseMetadataSchema,
});

export type AssembliesInventoryResponse = z.infer<
  typeof assembliesInventoryResponseSchema
>;

/**
 * Request filter schemas for inventory endpoints
 */

/**
 * Base filter options that can be sent to inventory endpoints
 * Field names match the backend JSON tags exactly
 */
export const inventoryFiltersSchema = z
  .object({
    itemKeys: z.array(z.string()).optional(),
    locationIds: z.array(z.string()).optional(),
    productKeys: z.array(z.string()).optional(),
    assetIds: z.array(z.string()).optional(),
    assetKeys: z.array(z.string()).optional(),
    includeNonInventory: z.boolean().optional(),
    includeInTransit: z.boolean().optional(),
    includeScrappedAssets: z.boolean().optional(),
    includeArchivedProducts: z.boolean().optional(),
    excludeArchivedLocations: z.boolean().optional(),
    excludeSerials: z.boolean().optional(),
  })
  .strict();

export type InventoryFilters = z.infer<typeof inventoryFiltersSchema>;

/**
 * Page size that asks a paged inventory list for every row
 */
export const UNPAGINATED_INVENTORY_LIMIT = -1;

/**
 * Schema for an inventory lot
 * Maps to Go InventoryLot - represents a single lot for an item at a location
 */
const inventoryLotSchema = z.object({
  id: z.string(),
  quantity: z.number(),
  batch: sparseBatchSchema,
});

export type InventoryLot = z.infer<typeof inventoryLotSchema>;

/**
 * Schema for inventory lots response
 * Maps to Go InventoryLotsResponse
 */
export const inventoryLotsResponseSchema = z.object({
  lots: z.array(inventoryLotSchema),
});

export type InventoryLotsResponse = z.infer<typeof inventoryLotsResponseSchema>;
